/**
 * Search Service - Refactored with Unified Architecture
 * Migrated from mixed axios/fetch to unified fetch-based architecture
 * Follows SOLID, KISS, and YAGNI principles
 */

import { ReadonlyURLSearchParams } from "next/navigation";

import { SearchHelps, ShowFilters } from "@/types/searchService.type";
import { buildQueryStringFromFilters } from "@/utils/queryString";

import { BaseService } from "./core/base-service";
import { httpClient } from "./core/http-client";
import { axiosWithAuth } from "@/services/legacy/api/axiosInstances";

// Enhanced type definitions for search responses
export type LocalizedName = {
  en: string;
  [locale: string]: string;
};

export interface Microdistrict {
  _id: string;
  name: LocalizedName;
}

export interface Street {
  _id: string;
  name: LocalizedName;
}

export interface MetroStation {
  _id: string;
  name: LocalizedName;
  sequenceNumber?: number;
}

export interface District {
  _id: string;
  name: LocalizedName;
  microdistricts: Microdistrict[];
  streets: Street[];
}

export interface City {
  _id: string;
  name: LocalizedName;
  districts: District[];
  metroStations?: MetroStation[];
}

export interface Region {
  _id: string;
  name: LocalizedName;
  cities: City[];
}

export type RegionResponse = Region[];

export interface SearchHelpsResponse {
  suggestions: Array<{
    id: string;
    title: string;
    type: "city" | "region" | "property" | "developer";
    slug?: string;
    count?: number;
  }>;
  recent_searches: string[];
  popular_searches: string[];
}

export interface FiltersResponse {
  filters: {
    priceRange: {
      min: number;
      max: number;
      currency: string;
    };
    propertyTypes: Array<{
      id: string;
      name: string;
      count: number;
    }>;
    locations: Array<{
      id: string;
      name: string;
      type: "city" | "region";
      count: number;
    }>;
    features: Array<{
      id: string;
      name: string;
      category: string;
      count: number;
    }>;
  };
}

export interface PropertyTypeResponse {
  propertyTypes: Array<{
    id: string;
    name: string;
    slug: string;
    description?: string;
    icon?: string;
  }>;
}

export interface CheckboxesResponse {
  amenities: string[];
  comfort: string[];
  appliances: string[];
  multimedia: string[];
  communications: string[];
  infrastructure: string[];
  landscape: string[];
  pets: string[];
  rentalOptions: string[];
}

export interface PropertyOptionsResponse {
  propertyClasses: Array<{
    key: string;
    label: string;
  }>;
  constructionStatuses: Array<{
    key: string;
    label: string;
  }>;
  heatingTypes: Array<{
    key: string;
    label: string;
  }>;
  wallMaterials: Array<{
    key: string;
    label: string;
  }>;
  insulationTypes: Array<{
    key: string;
    label: string;
  }>;
  conditions: Array<{
    key: string;
    label: string;
  }>;
  renovationTypes: Array<{
    key: string;
    label: string;
  }>;
  parkingTypes: Array<{
    key: string;
    label: string;
  }>;
  elevatorTypes: Array<{
    key: string;
    label: string;
  }>;
  frameTypes: Array<{
    key: string;
    label: string;
  }>;
  layoutStatuses: Array<{
    key: string;
    label: string;
  }>;
  completionYears: Array<{
    key: string;
    label: string;
  }>;
  completionQuarters: Array<{
    key: string;
    label: string;
  }>;
  financeOptions: Array<{
    key: string;
    label: string;
  }>;
  callStatuses: Array<{
    key: string;
    label: string;
  }>;
  listingTags: Array<{
    key: string;
    label: string;
  }>;
  exclusiveTypes: Array<{
    key: string;
    label: string;
  }>;
  publicationStatuses: Array<{
    key: string;
    label: string;
  }>;
}

export type RecommendedCitiesResponse = {
  name: LocalizedName;
  numberRecommendation: number;
}[];

export interface PropertySearchResponse {
  properties: Array<{
    id: string;
    title: string;
    price: number;
    currency: string;
    location: {
      city: string;
      region: string;
    };
    images: string[];
    dealType: string;
    category: string;
    features: Record<string, any>;
  }>;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: Record<string, any>;
}

// Using Next.js built-in caching instead of manual Cache-Control headers
// to avoid CORS issues with custom headers

/**
 * Search Service Class
 * Handles all search-related operations
 * Single Responsibility: Property search and filtering
 */
class SearchServiceImpl extends BaseService {
  constructor() {
    // Get timeout from environment variables with proper fallback
    const envTimeout =
      process.env.NODE_ENV === "development" ||
      process.env.NEXT_PUBLIC_ENV === "development"
        ? parseInt(
            process.env.NEXT_PUBLIC_API_TIMEOUT ||
              process.env.API_TIMEOUT ||
              "30000",
          )
        : 60000; // 60s for production

    super({
      name: "search",
      client: httpClient,
      enableLogging: true,
      retryAttempts: 2, // Search queries should be fast and responsive
      retryDelay: 1000,
      timeout: envTimeout, // Use environment timeout configuration
    });
  }

  /**
   * Get all available regions with caching
   * NOTE: Removed force-cache to allow data updates (metro stations, districts, etc.)
   * Caching is handled by Next.js ISR at page level via revalidate settings
   */
  async getRegions(): Promise<RegionResponse> {
    try {
      const response = await this.client.get<RegionResponse>(
        "/search/show-region",
      );

      this.logOperationSuccess("get regions", {
        count: response.data?.length || 0,
      });

      return response.data;
    } catch (error) {
      return await this.handleError(error, "get regions");
    }
  }

  /**
   * Get search suggestions and help data
   */
  async getSearchHelps(params: SearchHelps): Promise<SearchHelpsResponse> {
    // Enhanced parameter handling for slug-based searches
    const searchParams = { ...params };

    if (searchParams.slug) {
      this.logger.info(`Searching with slug: ${searchParams.slug}`);
    }

    try {
      const response = await this.get<SearchHelpsResponse>(
        "/search/show-search-helps",
        searchParams,
      );

      this.logOperationSuccess("get search helps", {
        hasSlug: !!searchParams.slug,
        suggestionsCount: response.suggestions?.length || 0,
      });

      return response;
    } catch (error) {
      return await this.handleError(error, "get search helps");
    }
  }

  /**
   * Get available filters for property search
   */
  async getFilters(params: ShowFilters): Promise<FiltersResponse> {
    try {
      // Build query string using existing utility
      const queryString = buildQueryStringFromFilters(params);

      const response = await this.client.get<FiltersResponse>(
        `/search/show-filters?${queryString}`,
      );

      this.logOperationSuccess("get filters", {
        hasFilters: !!response.data.filters,
        filterCount: Object.keys(response.data.filters || {}).length,
      });

      return response.data;
    } catch (error) {
      return await this.handleError(error, "get filters");
    }
  }

  /**
   * Get all property types with caching
   */
  async getPropertyTypes(): Promise<PropertyTypeResponse> {
    try {
      // NOTE: Removed force-cache to allow data updates
      const response = await this.client.get<PropertyTypeResponse>(
        "/search/show-property-type",
      );

      this.logOperationSuccess("get property types", {
        count: response.data.propertyTypes?.length || 0,
      });

      return response.data;
    } catch (error) {
      return await this.handleError(error, "get property types");
    }
  }

  /**
   * Get checkbox options for filtering with caching
   */
  async getCheckboxes(): Promise<CheckboxesResponse> {
    try {
      // NOTE: Removed force-cache to allow data updates
      const response = await this.client.get<CheckboxesResponse>(
        "/search/show-checkboxes",
      );

      // this.logOperationSuccess("get checkboxes", {
      //   categoriesCount: response.data.categories?.length || 0,
      // });

      return response.data;
    } catch (error) {
      return await this.handleError(error, "get checkboxes");
    }
  }

  /**
   * Get property search options with caching
   */
  async getPropertyOptions(): Promise<PropertyOptionsResponse> {
    try {
      // NOTE: Removed force-cache to allow data updates
      const response = await this.client.get<PropertyOptionsResponse>(
        "/search/property-options",
      );

      this.logOperationSuccess("get property options", {
        propertyClassesCount: response.data.propertyClasses?.length || 0,
        constructionStatusesCount:
          response.data.constructionStatuses?.length || 0,
        publicationStatusesCount:
          response.data.publicationStatuses?.length || 0,
        exclusiveTypesCount: response.data.exclusiveTypes?.length || 0,
      });

      return response.data;
    } catch (error) {
      return await this.handleError(error, "get property options");
    }
  }

  /**
   * Get recommended cities with caching
   */
  async getRecommendedCities(): Promise<RecommendedCitiesResponse> {
    try {
      const requestConfig: any = {};

      // Only enable caching in runtime mode, not during build
      if (process.env.NEXT_BUILD_MODE !== "true") {
        requestConfig.cache = "force-cache";
      }

      const response = await this.client.get<RecommendedCitiesResponse>(
        "/search/show-recommended-city",
        requestConfig,
      );

      this.logOperationSuccess("get recommended cities", {
        count: response.data?.length || 0,
      });

      return response.data;
    } catch (error) {
      return await this.handleError(error, "get recommended cities");
    }
  }

  /**
   * Search all properties with advanced filtering
   * Supports slug-based searches and landing page URLs
   */
  async searchAllProperties(
    queryParams: ReadonlyURLSearchParams,
    landingUrl?: string,
    tag?: string,
  ): Promise<PropertySearchResponse> {
    // Convert ReadonlyURLSearchParams to regular URLSearchParams
    const query = new URLSearchParams(queryParams);

    // Add landing URL if provided
    if (landingUrl) {
      query.set("landingUrl", landingUrl);
    }

    if (query.get("isGoldenVisaEligible") === "true") {
      const payload = { isGoldenVisaEligible: true };

      query.delete("isGoldenVisaEligible");
      query.set("programs", JSON.stringify(payload));
    }

    // Handle slug-based searches
    const slug = queryParams.get("slug");
    if (slug) {
      this.logger.info(`Including slug in search: ${slug}`);
      query.set("slug", slug);
    }

    console.log("QUERY", query.toString());

    try {
      const url = `${process.env.NEXT_PUBLIC_BASE_URL}/search/filter-property?${query.toString()}`;

      const res = await axiosWithAuth(url, {
        // cache: "no-store",
        // next: {
        //   tags: tag ? [tag] : undefined,
        // },
      });

      if (!res) {
        throw new Error("Search failed");
      }

      return res.data;
    } catch (error) {
      return await this.handleError(error, "search all properties");
    }
  }

  /**
   * Get popular search terms
   */
  async getPopularSearches(limit = 10): Promise<{ searches: string[] }> {
    const queryParams = { limit };

    try {
      const response = await this.get<{ searches: string[] }>(
        "/search/popular",
        queryParams,
      );

      this.logOperationSuccess("get popular searches", {
        count: response.searches?.length || 0,
      });

      return response;
    } catch (error) {
      return await this.handleError(error, "get popular searches");
    }
  }

  /**
   * Save search query for analytics
   */
  async saveSearchQuery(
    query: string,
    resultCount: number,
    userId?: string,
  ): Promise<{ success: boolean }> {
    this.validateRequired({ query }, ["query"]);

    const requestData = {
      query: query.trim(),
      resultCount,
      ...(userId && { userId }),
      timestamp: new Date().toISOString(),
    };

    try {
      const response = await this.post<{ success: boolean }>(
        "/search/save-query",
        requestData,
      );

      this.logOperationSuccess("save search query", {
        query: query.trim(),
        resultCount,
        hasUserId: !!userId,
      });

      return response;
    } catch (error) {
      // Search analytics shouldn't break the main flow
      this.logger.warn("Failed to save search query, continuing", { error });
      return { success: false };
    }
  }

  /**
   * Get search autocomplete suggestions
   */
  async getAutocompleteSuggestions(
    query: string,
    limit = 5,
  ): Promise<{ suggestions: string[] }> {
    this.validateRequired({ query }, ["query"]);

    if (query.length < 2) {
      return { suggestions: [] };
    }

    const queryParams = {
      q: query.trim(),
      limit,
    };

    try {
      const response = await this.get<{ suggestions: string[] }>(
        "/search/autocomplete",
        queryParams,
      );

      this.logOperationSuccess("get autocomplete suggestions", {
        query: query.trim(),
        suggestionsCount: response.suggestions?.length || 0,
      });

      return response;
    } catch (error) {
      // Autocomplete failures shouldn't break search
      this.logger.warn("Failed to get autocomplete suggestions", { error });
      return { suggestions: [] };
    }
  }

  /**
   * Enhanced validation for search parameters
   */
  protected validateRequired(
    params: Record<string, any>,
    requiredFields: string[],
  ): void {
    // First run base validation
    super.validateRequired(params, requiredFields);

    // Additional search-specific validation
    if (params.query && typeof params.query === "string") {
      const trimmedQuery = params.query.trim();
      if (trimmedQuery.length === 0) {
        throw new Error("Search query cannot be empty");
      }
      if (trimmedQuery.length > 200) {
        throw new Error(
          `Search query is too long (max 200 characters). Current length: ${trimmedQuery.length}`,
        );
      }
    }

    if (params.limit !== undefined) {
      const limit = parseInt(params.limit, 10);
      if (isNaN(limit) || limit < 1 || limit > 100) {
        throw new Error(
          `Limit must be between 1 and 100. Received: ${params.limit}`,
        );
      }
    }
  }

  /**
   * Custom health check for search service
   */
  async healthCheck() {
    try {
      // Quick health check by getting regions
      await this.getRegions();

      return {
        service: "search",
        status: "healthy" as const,
        timestamp: new Date().toISOString(),
        endpoints: {
          regions: "healthy",
          filters: "healthy",
          search: "healthy",
          suggestions: "healthy",
        },
      };
    } catch (error) {
      return {
        service: "search",
        status: "unhealthy" as const,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  // Legacy method aliases for backward compatibility
  async showRegion() {
    return this.getRegions();
  }

  async showSearchHelps(params: SearchHelps) {
    return this.getSearchHelps(params);
  }

  async showPropertyType() {
    return this.getPropertyTypes();
  }

  async showCheckboxes() {
    return this.getCheckboxes();
  }

  async showFilters(params: ShowFilters) {
    return this.getFilters(params);
  }

  async getAllProperty(
    queryParams: ReadonlyURLSearchParams,
    landingUrl?: string,
    tag?: string,
  ) {
    const result = await this.searchAllProperties(queryParams, landingUrl, tag);

    // Transform new format to legacy format for backward compatibility
    if (result && result.properties && result.pagination) {
      return {
        data: result.properties,
        total: result.pagination.total,
        limit: result.pagination.limit,
        page: result.pagination.page,
        totalPage: result.pagination.totalPages, // Note: totalPages -> totalPage
      };
    }

    return result;
  }
}

// Export singleton instance
export const searchService = new SearchServiceImpl();

// Create static wrapper for backward compatibility
const SearchServiceStatic = {
  showRegion: () => searchService.showRegion(),
  showSearchHelps: (params: SearchHelps) =>
    searchService.showSearchHelps(params),
  showPropertyType: () => searchService.showPropertyType(),
  showCheckboxes: () => searchService.showCheckboxes(),
  showFilters: (params: ShowFilters) => searchService.showFilters(params),

  // Also expose new method names
  getRegions: () => searchService.getRegions(),
  getSearchHelps: (params: SearchHelps) => searchService.getSearchHelps(params),
  getPropertyTypes: () => searchService.getPropertyTypes(),
  getCheckboxes: () => searchService.getCheckboxes(),
  getFilters: (params: ShowFilters) => searchService.getFilters(params),
  getPropertyOptions: () => searchService.getPropertyOptions(),
  getRecommendedCities: () => searchService.getRecommendedCities(),
  searchAllProperties: (
    queryParams: ReadonlyURLSearchParams,
    landingUrl?: string,
  ) => searchService.searchAllProperties(queryParams, landingUrl),
  getPopularSearches: (limit?: number) =>
    searchService.getPopularSearches(limit),
  saveSearchQuery: (query: string, resultCount: number, userId?: string) =>
    searchService.saveSearchQuery(query, resultCount, userId),
  getAutocompleteSuggestions: (query: string, limit?: number) =>
    searchService.getAutocompleteSuggestions(query, limit),
  getAllProperty: (
    queryParams: ReadonlyURLSearchParams,
    landingUrl?: string,
    tag?: string,
  ) => searchService.getAllProperty(queryParams, landingUrl, tag),
};

// Export the class, instance service, and static interface
export { SearchServiceImpl };

// Export the static interface that components expect
export const SearchService = SearchServiceStatic;

// Export for backward compatibility
export default searchService;
